import React, { useState } from 'react';
import { Container, Button, Form, Card, Alert, Row, Col } from 'react-bootstrap';
import DarkHeader from './common/DarkHeader';

function SettingsPage({ onBack, token, onLogout, userEmail }) {
    const [wpToken, setWpToken] = useState('');
    const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
    const [promptPassword, setPromptPassword] = useState('');
    const [promptLoading, setPromptLoading] = useState(false);
    const [promptError, setPromptError] = useState('');

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    const [newEmail, setNewEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);

    // Check if we need to fetch WordPress JWT on mount
    React.useEffect(() => {
        if (!wpToken) {
            setShowPasswordPrompt(true);
        }
    }, [wpToken]);

    const handlePasswordPromptSubmit = async (e) => {
        e.preventDefault();
        setPromptError('');
        
        if (!promptPassword) {
            setPromptError('Please enter your password');
            return;
        }

        
        setPromptLoading(true);
        try {
            const response = await fetch('/api/auth/fetch-wp-jwt.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: userEmail,
                    password: promptPassword
                })
            });
            const data = await response.json();
            
            if (response.ok && data.success) {
                setWpToken(data.wp_token);
                setShowPasswordPrompt(false);
                setPromptPassword('');
            } else {
                setPromptError(data.error || 'Failed to authenticate with WordPress');
            }
        } catch (err) {
            setPromptError('Network error: ' + err.message);
        } finally {
            setPromptLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (!oldPassword) {
            setPasswordError('Please enter your current password');
            return;
        }
        if (!newPassword) {
            setPasswordError('Please enter a new password');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            return;
        }

        
        setPasswordLoading(true);
        try {
            const response = await fetch('/api/users/change-password.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${wpToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: userEmail,
                    current_password: oldPassword,
                    new_password: newPassword,
                    new_password_confirm: confirmPassword
                })
            });
            const data = await response.json();
            
            if (response.ok && data.status === 'success') {
                // Show message briefly in passing, then log out immediately
                setPasswordSuccess('Password updated successfully. Logging out...');
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setWpToken('');
                onLogout();
            } else {
                setPasswordError(data.message || 'Change Password failed - contact admin');
            }
        } catch (err) {
            setPasswordError('Network error: ' + err.message);
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setEmailError('');
        setEmailSuccess('');

        if (!newEmail) {
            setEmailError('Please enter a new email address');
            return;
        }
        if (newEmail !== confirmEmail) {
            setEmailError('Email addresses do not match');
            return;
        }

        
        setEmailLoading(true);
        try {
            const response = await fetch('/api/users/change-email.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${wpToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: userEmail,
                    new_email: newEmail,
                    confirm_email: confirmEmail
                })
            });
            const data = await response.json();
            
            if (response.ok && data.status === 'success') {
                // Show message briefly in passing, then log out immediately
                setEmailSuccess('Email changed successfully. Logging out...');
                setNewEmail('');
                setConfirmEmail('');
                setWpToken('');
                onLogout();
            } else {
                setEmailError('Change Email failed - contact admin');
            }
        } catch (err) {
            setEmailError('Network error: ' + err.message);
        } finally {
            setEmailLoading(false);
        }
    };

    // Show password prompt modal if no WordPress JWT
    if (showPasswordPrompt) {
        return (
            <div className="page-container">
                <div className="bg-blur-indigo"></div>
                <div className="bg-blur-fuchsia"></div>
                
                <DarkHeader
                    title="Settings"
                    subtitle="Authentication required to change your account settings"
                    showBackButton={false}
                />

                <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                    <Row className="justify-content-center">
                        <Col md={6} lg={4}>
                            <Card className="glass">
                                <Card.Body>
                                    <Card.Title className="text-center mb-4">Authentication Required</Card.Title>
                                    <Card.Text className="text-center text-muted mb-4">
                                        To change your email or password, please re-enter your current password to authenticate.
                                    </Card.Text>
                                    
                                    <Form onSubmit={handlePasswordPromptSubmit}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Current Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Enter your current password"
                                                value={promptPassword}
                                                onChange={(e) => setPromptPassword(e.target.value)}
                                                disabled={promptLoading}
                                            />
                                        </Form.Group>
                                        
                                        {promptError && (
                                            <Alert variant="danger" className="mb-3">
                                                {promptError}
                                            </Alert>
                                        )}
                                        
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            className="w-100"
                                            type="submit"
                                            disabled={promptLoading}
                                        >
                                            {promptLoading ? 'Authenticating...' : 'Continue to Settings'}
                                        </Button>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="bg-blur-indigo"></div>
            <div className="bg-blur-fuchsia"></div>
            
            <DarkHeader
                title="Settings"
                subtitle="Manage your account settings and preferences"
                showBackButton={false}
            />

            <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                <Row>
                    <Col lg={6} className="mb-4">
                        <Card className="glass">
                            <Card.Body>
                                <h4 className="mb-3">Change Password</h4>
                                {passwordError && <Alert variant="danger">{passwordError}</Alert>}
                                {passwordSuccess && <Alert variant="success">{passwordSuccess}</Alert>}
                                <Form onSubmit={handlePasswordSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Current Password</Form.Label>
                                        <Form.Control type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} disabled={passwordLoading} />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>New Password</Form.Label>
                                        <Form.Control type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={passwordLoading} />
                                        <Form.Text className="text-muted">Must be at least 8 characters</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirm New Password</Form.Label>
                                        <Form.Control type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={passwordLoading} />
                                    </Form.Group>
                                    <Button variant="primary" type="submit" disabled={passwordLoading}>{passwordLoading ? 'Changing...' : 'Change Password'}</Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={6} className="mb-4">
                        <Card className="glass">
                            <Card.Body>
                                <h4 className="mb-3">Change Email</h4>
                                {emailError && <Alert variant="danger">{emailError}</Alert>}
                                {emailSuccess && <Alert variant="success">{emailSuccess}</Alert>}
                                <Form onSubmit={handleEmailSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>New Email Address</Form.Label>
                                        <Form.Control type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} disabled={emailLoading} />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirm New Email</Form.Label>
                                        <Form.Control type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} disabled={emailLoading} />
                                    </Form.Group>
                                    <Button variant="primary" type="submit" disabled={emailLoading}>{emailLoading ? 'Changing...' : 'Change Email'}</Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default SettingsPage;




